import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { BaseComponent } from 'src/app/components/BaseComponent';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { DashboardOpeService } from 'src/app/services/ope/dashboardOpe.service';
import { UserOpeService } from 'src/app/services/ope/userOpe.service';
import { ShareDashboardCB, ShareTargetResultDTO, ShareTargetStatusEnum } from 'src/app/ope/dashboard/ShareDashboardCB';
import { ShareValidationTargetDTO, ValidateShareDraftCB } from 'src/app/ope/dashboard/ValidateShareDraftCB';
import { ShareUserMetricGroupCB } from 'src/app/ope/user/metric/ShareUserMetricGroupCB';
import { ValidateShareUserMetricGroupDraftCB } from 'src/app/ope/user/metric/ValidateShareUserMetricGroupDraftCB';

/**
 * What is being duplicated. The two flows differ only in which ope pair they call and which
 * labels they show: the validate → review → share sequence is the same for both.
 */
export type DuplicateSubject = 'dashboard' | 'chart';

interface ValidationOutcome {
  invalidTargets: ShareValidationTargetDTO[];
  partialTargets: ShareValidationTargetDTO[];
}

interface ShareOutcome {
  results: ShareTargetResultDTO[];
  warningMessage?: string;
}

@Component({
  selector: 'app-duplicate-entity-popup',
  templateUrl: `duplicate-entity-popup.html`,
  styleUrls: ['./duplicate-entity-popup.css']
})
export class DuplicateEntityPopupComponent extends BaseComponent implements OnChanges {

  protected readonly dashboardService: DashboardService = inject(DashboardService);
  private readonly dashboardOpeService: DashboardOpeService = inject(DashboardOpeService);
  private readonly userOpeService: UserOpeService = inject(UserOpeService);

  @Input() subject: DuplicateSubject = 'dashboard';
  @Input() entityId?: number;
  @Input() originalName?: string;

  get popupVisible(): boolean {
    return this._popupVisible;
  }

  @Input() set popupVisible(value: boolean) {
    if (this._popupVisible === value)
      return;
    this._popupVisible = value;
    this.popupVisibleChange.emit(value);
  }

  @Output() popupVisibleChange = new EventEmitter<boolean>();
  @Output() entityDuplicated = new EventEmitter<void>();

  private _popupVisible: boolean = false;

  protected readonly MAX_NAME_LENGTH = this.gmService.STR_DIM_MID;

  protected selectedUserIds: number[] = [];
  protected newEntityName: string = '';

  protected reviewPopupVisible: boolean = false;
  protected skippedTargets: ShareValidationTargetDTO[] = [];
  protected partialTargets: ShareValidationTargetDTO[] = [];

  private submitting: boolean = false;

  /**
   * Runs after every input of the binding pass has been assigned: the popup opens before
   * entityId/originalName land, so the defaults can't be built in the visible setter.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['popupVisible']?.currentValue)
      this.initPopupValues();
  }

  protected isFormValid(): boolean {
    return !this.submitting && !!this.entityId && !!this.newEntityName.trim() && this.selectedUserIds.length > 0;
  }

  /**
   * Dry run before the real share: if every recipient would get a complete copy the share goes
   * through straight away, otherwise the user is shown who would get nothing and who would get only
   * part of the measures, and confirms again.
   */
  protected confirm() {
    if (!this.isFormValid())
      return;

    this.submitting = true;

    this.validateDraft().then((outcome) => {
      this.submitting = false;

      this.skippedTargets = outcome.invalidTargets ?? [];
      this.partialTargets = outcome.partialTargets ?? [];

      if (this.skippedTargets.length === 0 && this.partialTargets.length === 0) {
        this.share();
        return;
      }

      this.reviewPopupVisible = true;
    })
      // execOPE already toasts the error: the popup stays open so the user can retry
      .catch(() => this.submitting = false);
  }

  protected confirmReview() {
    this.reviewPopupVisible = false;
    this.share();
  }

  protected cancelReview() {
    this.reviewPopupVisible = false;
  }

  protected get titleKey(): string {
    return this.subjectKey('TITLE');
  }

  protected get nameKey(): string {
    return this.subjectKey('NAME');
  }

  /**
   * Il titolo dipende da cosa c'è da confermare: se nessun destinatario viene saltato la copia si fa
   * per tutti, e parlare di destinatari saltati sarebbe sbagliato.
   */
  protected get reviewTitleKey(): string {
    return this.skippedTargets.length === 0
      ? 'DUPLICATE_ENTITY.PARTIAL_TITLE'
      : 'DUPLICATE_ENTITY.SKIPPED_TITLE';
  }

  /**
   * The popup is hidden while the review dialog is up: that isn't the user closing it, and letting
   * popupVisible go false would reopen it as a new one, wiping name and recipients.
   */
  protected onPopupVisibleChange(visible: boolean) {
    if (this.reviewPopupVisible)
      return;
    this.popupVisible = visible;
  }

  protected skippedTargetLabel(target: ShareValidationTargetDTO): string {
    return `${this.targetName(target)} - ${this.translate.instant('DUPLICATE_ENTITY.SKIP_REASON.' + target.status)}`;
  }

  // I destinatari parziali hanno tutti la stessa ragione, già nell'intestazione della sezione:
  // ripeterla riga per riga sarebbe rumore
  protected targetName(target: ShareValidationTargetDTO): string {
    return target.username ?? String(target.userId);
  }

  protected cancel() {
    this.popupVisible = false;
  }

  private subjectKey(key: string): string {
    return `DUPLICATE_ENTITY.${this.subject === 'chart' ? 'CHART' : 'DASHBOARD'}.${key}`;
  }

  private validateDraft(): Promise<ValidationOutcome> {
    if (this.subject === 'chart') {
      const cb = new ValidateShareUserMetricGroupDraftCB();
      cb.i.userMetricGroupId = this.entityId!;
      cb.i.targetUserIds = this.selectedUserIds;
      return this.userOpeService.validateShareUserMetricGroupDraft(cb).then(() => cb.o);
    }

    const cb = new ValidateShareDraftCB();
    cb.i.dashboardId = this.entityId!;
    cb.i.targetUserIds = this.selectedUserIds;
    return this.dashboardOpeService.validateShareDraft(cb).then(() => cb.o);
  }

  private shareToTargets(name: string): Promise<ShareOutcome> {
    if (this.subject === 'chart') {
      const cb = new ShareUserMetricGroupCB();
      cb.i.userMetricGroupId = this.entityId!;
      cb.i.name = name;
      cb.i.targetUserIds = this.selectedUserIds;
      return this.userOpeService.shareUserMetricGroup(cb).then(() => cb.o);
    }

    const cb = new ShareDashboardCB();
    cb.i.dashboardId = this.entityId!;
    cb.i.name = name;
    cb.i.targetUserIds = this.selectedUserIds;
    return this.dashboardOpeService.shareDashboard(cb).then(() => cb.o);
  }

  private share() {
    this.submitting = true;

    this.shareToTargets(this.newEntityName.trim()).then((outcome) => {
      const copied = outcome.results.filter(result => result.status === ShareTargetStatusEnum.COPIED);

      if (copied.length > 0)
        this.toastService.openSuccessToastMessage(this.translate.instant(this.subjectKey('SHARED')));
      // partial success: skipped recipients are reported by the BE, the others are still copied
      if (!!outcome.warningMessage)
        this.toastService.openWarningToastMessage(outcome.warningMessage);

      this.popupVisible = false;

      // the select box only lists the displayed user's entities, the others need no reload
      if (copied.some(result => result.userId === this.dashboardService.displayedUserId))
        this.entityDuplicated.emit();
    })
      // execOPE already toasts the error: the popup stays open so the user can retry
      .catch(() => {
      })
      .finally(() => this.submitting = false);
  }

  private initPopupValues() {
    this.submitting = false;

    this.reviewPopupVisible = false;
    this.skippedTargets = [];
    this.partialTargets = [];

    this.selectedUserIds = [
      this.gmService.user.id
    ];

    const copyWord = this.translate.instant('COMMON.COPY');
    const suffix = ' - ' + copyWord;
    // a long source name plus the suffix would overflow the BE column: the source is what gets cut,
    // so the copy is still recognizable as one
    const roomForName = Math.max(this.MAX_NAME_LENGTH - suffix.length, 0);

    this.newEntityName = !this.originalName
      ? copyWord
      : this.originalName.slice(0, roomForName) + suffix;
  }

}
